import React from 'react';
import { getApiBaseUrl } from '../api/client';
import { reportError } from '../utils/sentry';
import './ErrorBoundary.css';

const CHUNK_RELOAD_KEY_PREFIX = 'debono:chunk-reload:';
const CHUNK_RELOAD_TTL_MS = 5 * 60 * 1000;
const CHUNK_ERROR_PATTERNS = [
  'chunkloaderror',
  'loading chunk',
  'failed to fetch dynamically imported module',
  'importing a module script failed',
];

function isChunkLoadError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  const name = String(error?.name || '').toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern) || name.includes(pattern));
}

function getChunkReloadKey() {
  return `${CHUNK_RELOAD_KEY_PREFIX}${window.location.pathname}`;
}

function hasRecentChunkReloadAttempt() {
  try {
    const raw = window.sessionStorage.getItem(getChunkReloadKey());
    if (!raw) return false;
    const timestamp = Number(raw);
    return Number.isFinite(timestamp) && Date.now() - timestamp < CHUNK_RELOAD_TTL_MS;
  } catch (_) {
    return false;
  }
}

function markChunkReloadAttempt() {
  try {
    window.sessionStorage.setItem(getChunkReloadKey(), String(Date.now()));
  } catch (_) {}
}

function clearChunkReloadAttempt() {
  try {
    window.sessionStorage.removeItem(getChunkReloadKey());
  } catch (_) {}
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
    const chunkError = isChunkLoadError(error);
    this.setState({ error, errorInfo, isChunkError: chunkError });

    reportError(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });

    if (chunkError && !hasRecentChunkReloadAttempt()) {
      markChunkReloadAttempt();
      window.location.reload();
      return;
    }

    // Report crash to backend (fire-and-forget, no auth required)
    try {
      const payload = {
        error: String(error),
        component_stack: errorInfo?.componentStack?.slice(0, 5000) || null,
        url: window.location.href,
        user_agent: navigator.userAgent,
      };
      const apiBase = getApiBaseUrl();
      fetch(`${apiBase}/api/bug-reports/crash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (_) {
      // never break the error boundary itself
    }
  }

  handleCopy = () => {
    const errorText = [
      this.state.error && this.state.error.toString(),
      '',
      'URL: ' + window.location.href,
      'Time: ' + new Date().toISOString(),
      '',
      'Component Stack:',
      this.state.errorInfo && this.state.errorInfo.componentStack,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {});
  };

  handleRefresh = () => {
    clearChunkReloadAttempt();
    window.location.reload();
  };

  handleReturnToDashboard = () => {
    clearChunkReloadAttempt();
    // Mount-aware: PUBLIC_URL=/debono under the moth hub, '' on root-served prod.
    window.location.assign(`${process.env.PUBLIC_URL || ''}/dashboard`);
  };

  render() {
    if (this.state.hasError) {
      const errorText = (this.state.error && this.state.error.toString()) || 'Unknown error';
      const stackText = (this.state.errorInfo && this.state.errorInfo.componentStack) || '';
      const isChunkError = this.state.isChunkError;
      return (
        <div className="error-boundary-shell">
          <div className={`error-boundary-card${isChunkError ? ' error-boundary-card--chunk' : ''}`}>
            <div className="error-boundary-eyebrow">
              {isChunkError ? 'Session refresh required' : 'Application error'}
            </div>
            <h2 className="error-boundary-title">
              {isChunkError ? 'This screen was updated while your tab was open' : 'Something went wrong'}
            </h2>
            <p className="error-boundary-message">
              {isChunkError
                ? 'Debono tried to load an older admin preview bundle after a deploy. Refresh to load the current version, or return to the dashboard and re-enter the screen.'
                : 'The application encountered an unexpected error. Refresh the page to continue.'}
            </p>
            <div className="error-boundary-details">
              <strong className="error-boundary-error">{errorText}</strong>
              {stackText && <div className="error-boundary-stack">{stackText}</div>}
            </div>
            <div className="error-boundary-actions">
              <button
                onClick={this.handleCopy}
                className={`error-boundary-button error-boundary-button--secondary${this.state.copied ? ' is-copied' : ''}`}
              >
                {this.state.copied ? 'Copied' : 'Copy Error'}
              </button>
              <button
                onClick={this.handleRefresh}
                className="error-boundary-button error-boundary-button--primary"
              >
                {isChunkError ? 'Refresh App' : 'Refresh Page'}
              </button>
              {isChunkError && (
                <button
                  onClick={this.handleReturnToDashboard}
                  className="error-boundary-button error-boundary-button--ghost"
                >
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
